import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CompanyDomain,
  CompanyDomainFilterInputObject,
  useCompanyDomainDestroyMutation,
  useCompanyDomainVerifyMutation,
  useSsoIntegrationActivateMutation,
  useSsoIntegrationDeactivateMutation,
  useSsoIntegrationDestroyMutation,
  useSsoIntegrationsDomainCheckLazyQuery,
} from 'graphql-common';
import toast from '@lib/components/Toast/Toast';
import {
  ORDERING_DIRECTION_ASC,
  OrderingParams,
  PAGINATION_PARAMS,
  PaginationParams,
} from '@lib/enums/urls';
import {
  Modals,
  UseModalManagementResult,
} from '@lib/hooks/useModalManagement';
import useCompanyDomainsQueryHook from 'utils/fetch/hooks/useCompanyDomainsQueryHook';
import useSsoIntegrationQueryHook from 'utils/fetch/hooks/useSsoIntegrationQueryHook';
import { APP_URLS } from 'constants/urls';

interface Props {
  id: string;
  useModalManagementResult: UseModalManagementResult;
}

export default function useSsoIntegrationViewActions({
  id,
  useModalManagementResult,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { closeModal, openModal } = useModalManagementResult;

  const ssoIntegrationQueryHookResult = useSsoIntegrationQueryHook({ id });
  const { refetch, data: ssoData } = ssoIntegrationQueryHookResult;

  // Domains query filtered by IDs from SSO integration
  const domainIds = useMemo(
    () => ssoData?.companyDomains?.map((d) => d.id) || [],
    [ssoData?.companyDomains],
  );

  const domainIdFilter = useMemo<CompanyDomainFilterInputObject[]>(
    () => (domainIds.length ? [{ id: { predicate: { in: domainIds } } }] : []),
    [domainIds],
  );

  const paginationState = useState<PaginationParams>(() => ({
    ...PAGINATION_PARAMS,
  }));
  const orderingState = useState<OrderingParams>(() => ({
    ordering_field: 'domain',
    ordering_direction: ORDERING_DIRECTION_ASC,
  }));
  const [paginationParams] = paginationState;
  const [orderingParams] = orderingState;

  const companyDomainsQueryHookResult = useCompanyDomainsQueryHook({
    additionalFilters: domainIdFilter,
    paginationParams,
    orderingParams,
    skip: !domainIds.length,
  });

  const [selectedDomain, setSelectedDomain] = useState<CompanyDomain | null>(
    null,
  );
  const [isDomainUsedBySso, setIsDomainUsedBySso] = useState(false);
  const [fetchSsoCheck] = useSsoIntegrationsDomainCheckLazyQuery();

  // SSO mutations
  const [activateMutation] = useSsoIntegrationActivateMutation({
    onCompleted: () => {
      refetch();
      toast({ content: t('activate-sso-connection-success') });
    },
    onError: (error) => {
      toast({ content: error.message || t('something-went-wrong') });
    },
  });

  const [deactivateMutation] = useSsoIntegrationDeactivateMutation({
    onCompleted: () => {
      refetch();
      toast({ content: t('deactivate-sso-connection-success') });
    },
    onError: (error) => {
      toast({ content: error.message || t('something-went-wrong') });
    },
  });

  const [destroyMutation] = useSsoIntegrationDestroyMutation({
    onCompleted: () => {
      closeModal(Modals.SsoIntegrationDestroyModal);
      toast({ content: t('destroy-sso-connection-success') });
      navigate(APP_URLS.dashboard.sso.index.path);
    },
    onError: (error) => {
      toast({ content: error.message || t('something-went-wrong') });
    },
  });

  // Company domain mutations
  const [verifyDomainMutation, verifyDomainMutationResult] =
    useCompanyDomainVerifyMutation({
      onCompleted: () => {
        closeModal(Modals.CompanyDomainVerifyModal);
        toast({ content: t('domain-verified-success') });
        refetch();
        companyDomainsQueryHookResult.refetch();
      },
      onError: (error) => {
        toast({ content: error.message || t('something-went-wrong') });
      },
    });

  const [destroyDomainMutation] = useCompanyDomainDestroyMutation({
    onCompleted: () => {
      closeModal(Modals.CompanyDomainDestroyModal);
      toast({ content: t('domain-deleted-success') });
      refetch();
      companyDomainsQueryHookResult.refetch();
    },
    onError: (error) => {
      toast({ content: error.message || t('something-went-wrong') });
    },
  });

  // Handlers
  const onEdit = () => {
    navigate(
      APP_URLS.dashboard.sso.edit.getDynamicPath({
        pathParts: { id },
      }),
    );
  };

  const onActivate = () => {
    if (id) activateMutation({ variables: { id } });
  };

  const onDeactivate = () => {
    if (id) deactivateMutation({ variables: { id } });
  };

  const onDelete = () => {
    openModal(Modals.SsoIntegrationDestroyModal);
  };

  const onConfirmedDelete = () => {
    if (id) destroyMutation({ variables: { id } });
  };

  const onVerifyDomain = (item?: CompanyDomain) => {
    if (item?.id) {
      setSelectedDomain(item);
      openModal(Modals.CompanyDomainVerifyModal);
    }
  };

  const onConfirmedVerifyDomain = () => {
    if (selectedDomain?.id) {
      verifyDomainMutation({ variables: { id: selectedDomain.id } });
    }
  };

  const onDeleteDomain = async (item?: CompanyDomain) => {
    if (item?.id) {
      setSelectedDomain(item);
      try {
        const { data: checkData } = await fetchSsoCheck({
          variables: {
            limit: 1,
            filters: [{ companyDomainsId: { predicate: { eq: item.id } } }],
          },
          fetchPolicy: 'network-only',
        });
        setIsDomainUsedBySso((checkData?.data?.collection?.length ?? 0) > 0);
      } catch {
        setIsDomainUsedBySso(false);
      }
      openModal(Modals.CompanyDomainDestroyModal);
    }
  };

  const onConfirmedDeleteDomain = () => {
    closeModal(Modals.CompanyDomainDestroyModal);
    if (selectedDomain?.id) {
      destroyDomainMutation({ variables: { id: selectedDomain.id } });
    }
  };

  return {
    ssoIntegrationQueryHookResult,
    companyDomainsQueryHookResult,
    paginationState,
    orderingState,
    isDomainUsedBySso,
    selectedDomain,
    verifyDomainMutationResult,
    onEdit,
    onActivate,
    onDeactivate,
    onDelete,
    onConfirmedDelete,
    onVerifyDomain,
    onConfirmedVerifyDomain,
    onDeleteDomain,
    onConfirmedDeleteDomain,
  };
}
